import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { type OddsGridIndexProps, OddsGridWithQuery } from '@cx-modules/odds-grid';

// Shared OddsGrid wrapper (NGF-6507), single source of truth for RR7 atlas and atlas-astro. Renders the
// @cx-modules/odds-grid module with the app-agnostic pre-configuration RR7's ~/modules/OddsGrid applied
// inline: i18n injection + an ErrorBoundary. Host-decoupled like OffersSlim/OffersList: the boundary
// takes an `onError` callback instead of importing @sentry/react-router (which @cx-web/components cannot
// depend on). RR7 injects captureException; atlas-astro injects a console shim.
export type OddsGridProps = Omit<OddsGridIndexProps, 'i18n'> & {
  onError?: (error: unknown) => void;
};

class OddsGridErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: unknown) => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError?.(error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export const OddsGrid = ({ onError, ...props }: OddsGridProps) => {
  const { i18n } = useTranslation();

  return (
    <OddsGridErrorBoundary onError={onError}>
      <OddsGridWithQuery {...props} i18n={i18n} />
    </OddsGridErrorBoundary>
  );
};
